import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Clock, X, ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const TrialSlotsPage = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [editingSlot, setEditingSlot] = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState({});

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/batches`, { withCredentials: true });
      setBatches(response.data);
      // Expand all batches by default
      const expanded = {};
      response.data.forEach(b => { expanded[b.batch_id] = true; });
      setExpandedBatches(expanded);
    } catch (error) {
      toast.error('Failed to fetch batches');
    } finally {
      setLoading(false);
    }
  };

  const toggleBatch = (batchId) => {
    setExpandedBatches(prev => ({
      ...prev,
      [batchId]: !prev[batchId]
    }));
  };

  const handleDeleteBatch = async (batchId, permanent = false) => {
    const message = permanent 
      ? 'Are you sure you want to PERMANENTLY DELETE this batch? This cannot be undone!'
      : 'Are you sure you want to deactivate this batch and all its slots?';
    
    if (!window.confirm(message)) return;
    
    try {
      const response = await axios.delete(`${API_URL}/api/batches/${batchId}?permanent=${permanent}`, { withCredentials: true });
      toast.success(response.data.message);
      if (response.data.leads_mapped > 0) {
        toast.info(`Note: ${response.data.leads_mapped} leads are still mapped to this batch`);
      }
      fetchBatches();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete batch';
      toast.error(errorMsg);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Are you sure you want to deactivate this slot?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/trial-slots/${slotId}`, { withCredentials: true });
      toast.success('Slot deactivated');
      fetchBatches();
    } catch (error) {
      toast.error('Failed to deactivate slot');
    }
  };

  return (
    <div data-testid="trial-slots-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Batches & Time Slots</h1>
          <p className="text-slate-500">Manage trial batches and their time slots</p>
        </div>
        <button
          onClick={() => {
            setEditingBatch(null);
            setShowBatchModal(true);
          }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-batch-btn"
        >
          <Plus className="w-4 h-4" />
          Add Batch
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : batches.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Layers className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No batches configured</p>
          <p className="text-sm">Create a batch to add time slots</p>
        </div>
      ) : (
        <div className="space-y-4">
          {batches.map((batch) => (
            <div
              key={batch.batch_id}
              className={`card overflow-hidden ${!batch.active_status && 'opacity-60'}`}
              data-testid={`batch-card-${batch.batch_id}`}
            >
              {/* Batch Header */}
              <div
                className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => toggleBatch(batch.batch_id)}
              >
                <div className="flex items-center gap-3">
                  {expandedBatches[batch.batch_id] ? (
                    <ChevronDown className="w-5 h-5 text-slate-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  )}
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Layers className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{batch.batch_name}</h3>
                    {batch.description && (
                      <p className="text-sm text-slate-500">{batch.description}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ml-2 ${
                    batch.active_status ? 'badge-success' : 'badge-error'
                  }`}>
                    {batch.active_status ? 'Active' : 'Inactive'}
                  </span>
                  <span className="text-sm text-slate-400 ml-2">
                    {batch.slots?.filter(s => s.active_status).length || 0} slots
                  </span>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {batch.active_status && (
                    <button
                      onClick={() => {
                        setSelectedBatchId(batch.batch_id);
                        setEditingSlot(null);
                        setShowSlotModal(true);
                      }}
                      className="px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md flex items-center gap-1"
                      data-testid={`add-slot-${batch.batch_id}`}
                    >
                      <Plus className="w-4 h-4" />
                      Add Slot
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setEditingBatch(batch);
                      setShowBatchModal(true);
                    }}
                    className="p-2 text-slate-600 hover:bg-slate-200 rounded-md"
                    data-testid={`edit-batch-${batch.batch_id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  {batch.active_status ? (
                    <button
                      onClick={() => handleDeleteBatch(batch.batch_id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                      title="Deactivate batch"
                      data-testid={`delete-batch-${batch.batch_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API_URL}/api/batches/${batch.batch_id}`, { active_status: true }, { withCredentials: true });
                            toast.success('Batch reactivated');
                            fetchBatches();
                          } catch (error) {
                            const errorMsg = error.response?.data?.detail || 'Failed to reactivate batch';
                            toast.error(errorMsg);
                          }
                        }}
                        className="px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-md"
                        title="Reactivate batch"
                        data-testid={`reactivate-batch-${batch.batch_id}`}
                      >
                        Reactivate
                      </button>
                      <button
                        onClick={() => handleDeleteBatch(batch.batch_id, true)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                        title="Permanently delete batch"
                        data-testid={`permanent-delete-batch-${batch.batch_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Slots */}
              {expandedBatches[batch.batch_id] && (
                <div className="p-4 border-t border-slate-200">
                  {batch.slots?.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No slots in this batch</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {batch.slots?.map((slot) => (
                        <div
                          key={slot.slot_id}
                          className={`p-4 rounded-lg border ${
                            slot.active_status ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'
                          }`}
                          data-testid={`slot-card-${slot.slot_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                slot.active_status ? 'bg-emerald-100' : 'bg-slate-100'
                              }`}>
                                <Clock className={`w-4 h-4 ${slot.active_status ? 'text-emerald-600' : 'text-slate-400'}`} />
                              </div>
                              <div>
                                <h4 className="font-medium text-slate-900">{slot.slot_name}</h4>
                                <p className="text-sm text-slate-500">
                                  {slot.slot_time_start} - {slot.slot_time_end}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => {
                                  setSelectedBatchId(batch.batch_id);
                                  setEditingSlot(slot);
                                  setShowSlotModal(true);
                                }}
                                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                                data-testid={`edit-slot-${slot.slot_id}`}
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                              {slot.active_status && (
                                <button
                                  onClick={() => handleDeleteSlot(slot.slot_id)}
                                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                                  data-testid={`delete-slot-${slot.slot_id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-slate-400">
                            Capacity: {slot.max_capacity}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Batch Modal */}
      {showBatchModal && (
        <BatchModal
          batch={editingBatch}
          onClose={() => {
            setShowBatchModal(false);
            setEditingBatch(null);
          }}
          onSave={() => {
            setShowBatchModal(false);
            setEditingBatch(null);
            fetchBatches();
          }}
        />
      )}

      {/* Slot Modal */}
      {showSlotModal && (
        <SlotModal
          slot={editingSlot}
          batchId={selectedBatchId}
          onClose={() => {
            setShowSlotModal(false);
            setEditingSlot(null);
            setSelectedBatchId(null);
          }}
          onSave={() => {
            setShowSlotModal(false);
            setEditingSlot(null);
            setSelectedBatchId(null);
            fetchBatches();
          }}
        />
      )}
    </div>
  );
};

const BatchModal = ({ batch, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    batch_name: batch?.batch_name || '',
    description: batch?.description || '',
    active_status: batch?.active_status !== false // default true for new batches
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (batch) {
        await axios.put(`${API_URL}/api/batches/${batch.batch_id}`, formData, { withCredentials: true });
        toast.success('Batch updated');
      } else {
        await axios.post(`${API_URL}/api/batches`, formData, { withCredentials: true });
        toast.success('Batch created');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save batch');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">
            {batch ? 'Edit Batch' : 'Add New Batch'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Batch Name *</label>
            <input
              type="text"
              required
              value={formData.batch_name}
              onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
              className="input-base"
              placeholder="e.g., Morning Batch, Evening Batch"
              data-testid="batch-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input-base"
              placeholder="Optional description"
            />
          </div>
          
          {/* Active Status Toggle - only show when editing */}
          {batch && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <label className="font-medium text-slate-700">Batch Status</label>
                <p className="text-sm text-slate-500">
                  {formData.active_status ? 'This batch is active and visible' : 'This batch is inactive and hidden'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, active_status: !formData.active_status })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.active_status ? 'bg-green-500' : 'bg-slate-300'
                }`}
                data-testid="batch-status-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.active_status ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="save-batch-btn">
              {saving ? 'Saving...' : (batch ? 'Update Batch' : 'Create Batch')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SlotModal = ({ slot, batchId, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    batch_id: slot?.batch_id || batchId,
    slot_name: slot?.slot_name || '',
    slot_time_start: slot?.slot_time_start || '09:00',
    slot_time_end: slot?.slot_time_end || '10:00',
    max_capacity: slot?.max_capacity || 10
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (slot) {
        await axios.put(`${API_URL}/api/trial-slots/${slot.slot_id}`, formData, { withCredentials: true });
        toast.success('Slot updated');
      } else {
        await axios.post(`${API_URL}/api/trial-slots`, formData, { withCredentials: true });
        toast.success('Slot created');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save slot');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">
            {slot ? 'Edit Slot' : 'Add New Slot'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Slot Name *</label>
            <input
              type="text"
              required
              value={formData.slot_name}
              onChange={(e) => setFormData({ ...formData, slot_name: e.target.value })}
              className="input-base"
              placeholder="e.g., 9 AM - 10 AM"
              data-testid="slot-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Time *</label>
              <input
                type="time"
                required
                value={formData.slot_time_start}
                onChange={(e) => setFormData({ ...formData, slot_time_start: e.target.value })}
                className="input-base"
                data-testid="slot-start-time"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">End Time *</label>
              <input
                type="time"
                required
                value={formData.slot_time_end}
                onChange={(e) => setFormData({ ...formData, slot_time_end: e.target.value })}
                className="input-base"
                data-testid="slot-end-time"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Max Capacity</label>
            <input
              type="number"
              min="1"
              value={formData.max_capacity}
              onChange={(e) => setFormData({ ...formData, max_capacity: parseInt(e.target.value) || 10 })}
              className="input-base"
              data-testid="slot-capacity"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="save-slot-btn">
              {saving ? 'Saving...' : (slot ? 'Update Slot' : 'Create Slot')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrialSlotsPage;
